import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ComponentProps } from 'react';
import moment from 'moment';
import SvgIcon from '@/components/Icon/SvgIcon';

// Main compound component
const EventInfoCard = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof Card>) => (
  <Card
    ref={ref}
    className={cn(
      'tw:w-[327px] tw:flex tw:flex-col tw:p-4 tw:shadow-sm',
      className,
    )}
    {...props}
  />
);
EventInfoCard.displayName = 'EventInfoCard';

// Status badge component
const EventInfoCardStatus = ({
  className,
  status,
  children,
  ...props
}: React.ComponentProps<typeof Badge> & {
  className?: string;
  status: 'planned' | 'ongoing' | 'finished';
  children?: React.ReactNode;
}) => {
  const statusConfig: {
    [key in 'planned' | 'ongoing' | 'finished']: {
      variant: ComponentProps<typeof Badge>['variant'];
      className: string;
    };
  } = {
    planned: {
      variant: 'default',
      className:
        'tw:bg-neutral-50 tw:font-[500] tw:shadow-none tw:hover:bg-neutral-50 tw:text-neutral-900 tw:self-start',
    },
    ongoing: {
      variant: 'default',
      className:
        'tw:bg-lilac-500 tw:font-[500] tw:shadow-none tw:hover:bg-lilac-500 tw:text-white tw:self-start',
    },
    finished: {
      variant: 'default',
      className:
        'tw:text-muted-foreground tw:font-[500] tw:shadow-none tw:bg-transparent tw:hover:bg-transparent tw:self-start',
    },
  };

  const config = statusConfig[status];

  return (
    <CardHeader className="tw:p-0 tw:px-0">
      <Badge
        variant={config.variant}
        className={cn(config.className, className)}
        {...props}
      >
        {children || status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    </CardHeader>
  );
};
EventInfoCardStatus.displayName = 'EventInfoCardStatus';

// Title component
const EventInfoCardTitle = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<'h3'>) => (
  <CardContent className="tw:p-0 tw:py-6 tw:flex-1">
    <h3
      ref={ref}
      className={cn(
        'tw:text-2xl tw:leading-tight tw:font-bold tw:text-balance tw:line-clamp-2 tw:mb-0!',
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  </CardContent>
);
EventInfoCardTitle.displayName = 'EventInfoCardTitle';

// Date component
const EventInfoCardDate = ({
  ref,
  className,
  startDate,
  endDate,
  ...props
}: React.ComponentProps<typeof CardContent> & {
  startDate: string;
  endDate: string;
}) => (
  <CardContent className="tw:p-0">
    <div
      ref={ref}
      className={cn(
        'tw:text-muted-foreground tw:flex tw:items-center tw:gap-2',
        className,
      )}
      {...props}
    >
      <div className="tw:flex tw:items-center tw:justify-center tw:p-[7px] tw:bg-secondary tw:rounded-md">
        <SvgIcon name="calendar" size={16} className="tw:text-primary" />
      </div>
      <span className="tw:text-sm tw:text-neutral-900 tw:dark:text-white">
        {moment(startDate).format('DD.MM')}
        {' - '}
        {moment(endDate).format('DD.MM.YYYY')}
      </span>
    </div>
  </CardContent>
);
EventInfoCardDate.displayName = 'EventInfoCardDate';

// Actions component (for buttons)
const EventInfoCardActions = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof CardFooter>) => (
  <CardFooter
    ref={ref}
    className={cn('tw:justify-end tw:p-0 tw:pt-0', className)}
    {...props}
  />
);
EventInfoCardActions.displayName = 'EventInfoCardActions';

export {
  EventInfoCard,
  EventInfoCardStatus,
  EventInfoCardTitle,
  EventInfoCardDate,
  EventInfoCardActions,
};
